/**
 * Hooks Command Group
 * jai1 hooks (j hooks)
 *
 * Install/remove Git hooks for jai1 integrations.
 * Currently supports: auto task sync on push/pull.
 * Extensible for future hook integrations.
 */
import { Command } from 'commander';
import { existsSync, readFileSync, writeFileSync, unlinkSync, chmodSync, mkdirSync } from 'fs';
import { join } from 'path';
import { execSync } from 'child_process';
import chalk from 'chalk';

// Marker used to identify jai1-managed hook sections
const MARKER_START = '# >>> jai1-hooks';
const MARKER_END = '# <<< jai1-hooks';

const SHEBANG = '#!/bin/sh';

/**
 * Hook definitions — each entry describes what to inject into a Git hook file.
 * To add hooks in the future, just add entries here.
 */
const HOOK_DEFINITIONS: { hookName: string; description: string; script: string }[] = [
    {
        hookName: 'post-merge',
        description: 'Auto-pull tasks after git pull',
        script: [
            '# task sync: pull after merge',
            'if command -v j &>/dev/null; then j t sync --pull 2>/dev/null',
            'elif command -v jai1 &>/dev/null; then jai1 t sync --pull 2>/dev/null',
            'fi || true',
        ].join('\n'),
    },
    {
        hookName: 'pre-push',
        description: 'Auto-push tasks before git push',
        script: [
            '# task sync: push before push',
            'if command -v j &>/dev/null; then j t sync --push 2>/dev/null',
            'elif command -v jai1 &>/dev/null; then jai1 t sync --push 2>/dev/null',
            'fi || true',
        ].join('\n'),
    },
];

/**
 * Check if git is installed.
 */
function isGitInstalled(): boolean {
    try {
        execSync('git --version', { stdio: ['pipe', 'pipe', 'pipe'] });
        return true;
    } catch {
        return false;
    }
}

/**
 * Get the .git/hooks directory path, resolving git worktrees.
 */
function getGitHooksDir(): string {
    if (!isGitInstalled()) {
        throw new Error('Git chưa được cài đặt. Hãy cài git trước khi sử dụng hooks.');
    }

    try {
        const gitDir = execSync('git rev-parse --git-common-dir', {
            encoding: 'utf-8',
            stdio: ['pipe', 'pipe', 'pipe'],
        }).trim();
        return join(gitDir, 'hooks');
    } catch {
        throw new Error('Thư mục hiện tại không phải git repository. Hãy chạy lệnh này trong một git repo.');
    }
}

/**
 * Build the jai1 section to inject into a hook file.
 */
function buildSection(script: string): string {
    return `${MARKER_START}\n${script}\n${MARKER_END}`;
}

/**
 * Check if a hook file already has a jai1 section.
 */
function hasJai1Section(content: string): boolean {
    return content.includes(MARKER_START);
}

/**
 * Remove the jai1 section from hook content.
 */
function removeJai1Section(content: string): string {
    const lines = content.split('\n');
    const result: string[] = [];
    let inSection = false;

    for (const line of lines) {
        if (line.trim() === MARKER_START) {
            inSection = true;
            continue;
        }
        if (line.trim() === MARKER_END) {
            inSection = false;
            continue;
        }
        if (!inSection) {
            result.push(line);
        }
    }

    return result.join('\n');
}

/**
 * Check if a hook file is effectively empty (only shebang + whitespace).
 */
function isEffectivelyEmpty(content: string): boolean {
    const stripped = content
        .split('\n')
        .filter((line) => line.trim() !== '' && line.trim() !== SHEBANG)
        .join('');
    return stripped.length === 0;
}

/**
 * Setup: install all jai1 hooks.
 */
function setupHooks(): void {
    const hooksDir = getGitHooksDir();

    // Ensure hooks directory exists
    if (!existsSync(hooksDir)) {
        mkdirSync(hooksDir, { recursive: true });
    }

    let installed = 0;
    let skipped = 0;

    for (const def of HOOK_DEFINITIONS) {
        const hookPath = join(hooksDir, def.hookName);
        const section = buildSection(def.script);

        try {
            if (existsSync(hookPath)) {
                const existing = readFileSync(hookPath, 'utf-8');

                if (hasJai1Section(existing)) {
                    console.log(chalk.dim(`  ⏭  ${def.hookName} — đã có, skip`));
                    skipped++;
                    continue;
                }

                // Append to existing hook
                const updated = existing.trimEnd() + '\n\n' + section + '\n';
                writeFileSync(hookPath, updated);
            } else {
                // Create new hook file
                writeFileSync(hookPath, `${SHEBANG}\n\n${section}\n`);
            }

            chmodSync(hookPath, 0o755);
            console.log(chalk.green(`  ✅ ${def.hookName} — ${def.description}`));
            installed++;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes('EACCES') || msg.includes('permission')) {
                console.log(chalk.red(`  ❌ ${def.hookName} — không có quyền ghi. Thử chạy với sudo.`));
            } else {
                console.log(chalk.red(`  ❌ ${def.hookName} — lỗi: ${msg}`));
            }
        }
    }

    console.log('');
    if (installed > 0) {
        console.log(chalk.green(`🎉 Đã cài ${installed} hook(s).`));
        console.log(chalk.dim('   Tasks sẽ tự động sync khi bạn push/pull.'));
    } else {
        console.log(chalk.dim('ℹ️  Tất cả hooks đã được cài sẵn.'));
    }
}

/**
 * Remove: uninstall jai1-managed hook sections.
 */
function removeHooks(): void {
    const hooksDir = getGitHooksDir();
    let removed = 0;

    for (const def of HOOK_DEFINITIONS) {
        const hookPath = join(hooksDir, def.hookName);

        if (!existsSync(hookPath)) {
            continue;
        }

        try {
            const content = readFileSync(hookPath, 'utf-8');

            if (!hasJai1Section(content)) {
                continue;
            }

            const cleaned = removeJai1Section(content);

            if (isEffectivelyEmpty(cleaned)) {
                // Hook file only had jai1 content — delete it
                unlinkSync(hookPath);
                console.log(chalk.yellow(`  🗑  ${def.hookName} — xoá file (chỉ có jai1 hooks)`));
            } else {
                writeFileSync(hookPath, cleaned);
                console.log(chalk.yellow(`  ✂️  ${def.hookName} — gỡ phần jai1`));
            }
            removed++;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes('EACCES') || msg.includes('permission')) {
                console.log(chalk.red(`  ❌ ${def.hookName} — không có quyền ghi/xoá. Thử chạy với sudo.`));
            } else {
                console.log(chalk.red(`  ❌ ${def.hookName} — lỗi: ${msg}`));
            }
        }
    }

    console.log('');
    if (removed > 0) {
        console.log(chalk.green(`✅ Đã gỡ ${removed} hook(s).`));
    } else {
        console.log(chalk.dim('ℹ️  Không có jai1 hooks nào để gỡ.'));
    }
}

export function createHooksCommand(): Command {
    const cmd = new Command('hooks')
        .description('Quản lý Git hooks tích hợp cho jai1');

    cmd.command('setup')
        .description('Cài đặt Git hooks (auto task sync on push/pull)')
        .action(() => {
            console.log(chalk.bold('\n🔗 Cài đặt jai1 Git hooks...\n'));
            setupHooks();
            console.log('');
        });

    cmd.command('remove')
        .description('Gỡ bỏ jai1 Git hooks')
        .action(() => {
            console.log(chalk.bold('\n🔗 Gỡ bỏ jai1 Git hooks...\n'));
            removeHooks();
            console.log('');
        });

    // Default: show help
    cmd.action(() => {
        cmd.help();
    });

    return cmd;
}
